import React, {useMemo} from 'react';

import {useCertificationBundleQuery} from '@/generated/graphql';
import {
    BackendSourceOptions,
    BrowseEventTrackingProvider,
} from '@udemy-v2/react-browse-event-tracking';
import {FrequentlyBoughtTogether} from '@udemy-v2/react-bundle-unit';
import {AddToCartButton} from '@udemy-v2/react-shopping-buttons';
import {generateTrackingId} from '@udemy-v2/event-tracking';
import {useI18n} from '@udemy-v2/i18n';
import type {Course} from '@udemy-v2/browse-course';

interface CertificationBundleProps {
    bundleCourseIds: string[];
}

export const CertificationBundle = ({bundleCourseIds}: CertificationBundleProps) => {
    const {gettext} = useI18n();
    const {data, isLoading} = useCertificationBundleQuery({
        ids: bundleCourseIds,
    });

    // TODO: Move this adaptation code into a wrapper around the GQL hook.
    // Do this to minimize the chance of a course being retrieved without a tracking id attached to it.
    const courses = useMemo(() => {
        // Filter out courses and ensure an array exists
        const filteredCourses =
            data?.courses?.filter(
                (course): course is NonNullable<typeof course> => course !== null,
            ) ?? [];

        return filteredCourses.map((course) => ({
            ...(course as Course),
            frontendTrackingId: generateTrackingId(),
        }));
    }, [data?.courses]);

    // Don't render if loading or no courses
    if (isLoading || courses.length === 0) {
        return null;
    }

    return (
        <BrowseEventTrackingProvider
            browseEventTrackingContext={{backendSource: BackendSourceOptions.DISCOVERY}}
        >
            <FrequentlyBoughtTogether
                title={gettext('Prepare for your certification exam with confidence')}
                description={gettext(
                    'Get this top-rated course, plus a collection of practice tests, to prepare for your exam in one convenient bundle!',
                )}
                courses={courses as Course[]}
                options={{openInNewTab: true, hideDescription: true}}
                addToCartButton={
                    <AddToCartButton
                        buyables={courses.map((course) => ({
                            id: Number(course.id),
                            buyableObjectType: 'course',
                            frontendTrackingId: course.frontendTrackingId,
                        }))}
                        addToCartButtonText={gettext('Add all to cart')}
                        forceRedirectToCart={true}
                        buttonStyleProps={{
                            size: 'large',
                        }}
                    />
                }
            />
        </BrowseEventTrackingProvider>
    );
};
